import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  RotateCw, RotateCcw, FlipHorizontal, FlipVertical,
  Crop, Save, Download, Undo2, X, Check, Sun, Contrast, Droplets
} from 'lucide-react';
import { useLibrary } from '../contexts/LibraryContext';
import type { MedaImage } from '../types/image';

interface Adjustments {
  brightness: number;
  contrast: number;
  saturation: number;
}

interface CropState {
  active: boolean;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

const defaultAdjustments: Adjustments = { brightness: 100, contrast: 100, saturation: 100 };

export default function BasicEditorPage() {
  const { imageId } = useParams<{ imageId: string }>();
  const navigate = useNavigate();
  const { getImage, addImage } = useLibrary();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [image, setImage] = useState<MedaImage | null>(null);
  const [bitmap, setBitmap] = useState<ImageBitmap | null>(null);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);
  const [adjustments, setAdjustments] = useState<Adjustments>(defaultAdjustments);
  const [crop, setCrop] = useState<CropState>({ active: false, startX: 0, startY: 0, endX: 0, endY: 0 });
  const [cropping, setCropping] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load image
  useEffect(() => {
    if (!imageId) return;
    getImage(imageId).then(img => {
      if (img) {
        setImage(img);
        createImageBitmap(img.blob).then(setBitmap);
      }
    });
  }, [imageId, getImage]);

  // Render canvas
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !bitmap) return;

    const isRotated = rotation % 180 !== 0;
    const w = isRotated ? bitmap.height : bitmap.width;
    const h = isRotated ? bitmap.width : bitmap.height;

    // Fit to container
    const container = containerRef.current;
    if (!container) return;
    const maxW = container.clientWidth - 40;
    const maxH = container.clientHeight - 40;
    const scale = Math.min(maxW / w, maxH / h, 1);

    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    // Apply CSS filter for adjustments
    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;

    // Transform
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.drawImage(bitmap, -bitmap.width * scale / 2, -bitmap.height * scale / 2, bitmap.width * scale, bitmap.height * scale);
    ctx.restore();

    // Draw crop overlay
    if (cropping && crop.active) {
      const x1 = Math.min(crop.startX, crop.endX);
      const y1 = Math.min(crop.startY, crop.endY);
      const x2 = Math.max(crop.startX, crop.endX);
      const y2 = Math.max(crop.startY, crop.endY);

      // Darken outside crop area
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, canvas.width, y1);
      ctx.fillRect(0, y1, x1, y2 - y1);
      ctx.fillRect(x2, y1, canvas.width - x2, y2 - y1);
      ctx.fillRect(0, y2, canvas.width, canvas.height - y2);

      // Crop border
      ctx.strokeStyle = 'var(--sage-primary)';
      ctx.lineWidth = 2;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
    }
  }, [bitmap, rotation, flipH, flipV, adjustments, cropping, crop]);

  useEffect(() => { render(); }, [render]);

  // Resize handler
  useEffect(() => {
    const onResize = () => render();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [render]);

  // Crop mouse handlers
  const onMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!cropping) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setCrop({ active: true, startX: x, startY: y, endX: x, endY: y });
    setIsDragging(true);
  }, [cropping]);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDragging || !cropping) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    setCrop(prev => ({ ...prev, endX: e.clientX - rect.left, endY: e.clientY - rect.top }));
  }, [isDragging, cropping]);

  const onMouseUp = useCallback(() => { setIsDragging(false); }, []);

  // Apply crop
  const applyCrop = useCallback(async () => {
    if (!bitmap || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const x1 = Math.min(crop.startX, crop.endX);
    const y1 = Math.min(crop.startY, crop.endY);
    const cw = Math.abs(crop.endX - crop.startX);
    const ch = Math.abs(crop.endY - crop.startY);
    if (cw < 10 || ch < 10) return;

    // Get the crop in original image coordinates
    const scaleX = bitmap.width / canvas.width;
    const scaleY = bitmap.height / canvas.height;

    const offscreen = new OffscreenCanvas(Math.round(cw * scaleX), Math.round(ch * scaleY));
    const ctx = offscreen.getContext('2d')!;
    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
    ctx.drawImage(bitmap, x1 * scaleX, y1 * scaleY, cw * scaleX, ch * scaleY, 0, 0, offscreen.width, offscreen.height);

    const blob = await offscreen.convertToBlob({ type: 'image/png' });
    const newBitmap = await createImageBitmap(blob);
    setBitmap(newBitmap);
    setCropping(false);
    setCrop({ active: false, startX: 0, startY: 0, endX: 0, endY: 0 });
    setAdjustments(defaultAdjustments);
  }, [bitmap, crop, adjustments]);

  // Export current state to blob
  const exportCanvas = useCallback(async (): Promise<Blob> => {
    if (!bitmap) throw new Error('No image');
    const isRotated = rotation % 180 !== 0;
    const w = isRotated ? bitmap.height : bitmap.width;
    const h = isRotated ? bitmap.width : bitmap.height;

    const offscreen = new OffscreenCanvas(w, h);
    const ctx = offscreen.getContext('2d')!;
    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
    ctx.translate(w / 2, h / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.drawImage(bitmap, -bitmap.width / 2, -bitmap.height / 2);

    return offscreen.convertToBlob({ type: 'image/png' });
  }, [bitmap, rotation, flipH, flipV, adjustments]);

  const handleSave = useCallback(async () => {
    if (!image) return;
    setSaving(true);
    const blob = await exportCanvas();
    await addImage(blob, `${image.name} (edited)`, 'edited', image.id, 'basic_edit');
    setSaving(false);
    navigate('/library');
  }, [image, exportCanvas, addImage, navigate]);

  const handleDownload = useCallback(async () => {
    const blob = await exportCanvas();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = image?.name || 'edited.png';
    a.click();
    URL.revokeObjectURL(url);
  }, [exportCanvas, image]);

  if (!image || !bitmap) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--sage-text-muted)' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div
        className="flex items-center gap-2 px-4 py-3 border-b flex-wrap"
        style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
      >
        <ToolBtn icon={<RotateCcw size={16} />} label="Rotate Left" onClick={() => setRotation(r => (r - 90 + 360) % 360)} />
        <ToolBtn icon={<RotateCw size={16} />} label="Rotate Right" onClick={() => setRotation(r => (r + 90) % 360)} />
        <ToolBtn icon={<FlipHorizontal size={16} />} label="Flip H" onClick={() => setFlipH(f => !f)} active={flipH} />
        <ToolBtn icon={<FlipVertical size={16} />} label="Flip V" onClick={() => setFlipV(f => !f)} active={flipV} />
        <div className="w-px h-6 mx-1" style={{ backgroundColor: 'var(--sage-white-10)' }} />
        <ToolBtn icon={<Crop size={16} />} label="Crop" onClick={() => { setCropping(!cropping); setCrop({ active: false, startX: 0, startY: 0, endX: 0, endY: 0 }); }} active={cropping} />
        {cropping && crop.active && (
          <>
            <ToolBtn icon={<Check size={16} />} label="Apply Crop" onClick={applyCrop} accent />
            <ToolBtn icon={<X size={16} />} label="Cancel" onClick={() => { setCropping(false); setCrop({ active: false, startX: 0, startY: 0, endX: 0, endY: 0 }); }} />
          </>
        )}
        <div className="flex-1" />
        <ToolBtn icon={<Undo2 size={16} />} label="Reset" onClick={() => { setRotation(0); setFlipH(false); setFlipV(false); setAdjustments(defaultAdjustments); }} />
        <ToolBtn icon={<Download size={16} />} label="Download" onClick={handleDownload} />
        <ToolBtn icon={<Save size={16} />} label={saving ? 'Saving...' : 'Save to Library'} onClick={handleSave} accent disabled={saving} />
      </div>

      {/* Canvas + adjustments */}
      <div className="flex flex-1 min-h-0">
        {/* Canvas area */}
        <div ref={containerRef} className="flex-1 flex items-center justify-center p-5 min-w-0">
          <canvas
            ref={canvasRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            className="rounded-lg shadow-lg"
            style={{ cursor: cropping ? 'crosshair' : 'default', maxWidth: '100%', maxHeight: '100%' }}
          />
        </div>

        {/* Adjustments panel */}
        <div
          className="w-56 p-4 border-l flex flex-col gap-5 overflow-y-auto"
          style={{ borderColor: 'var(--sage-white-5)', backgroundColor: 'var(--sage-sidebar)' }}
        >
          <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: 'var(--sage-text-muted)' }}>Adjustments</p>
          <AdjustSlider
            icon={<Sun size={14} />}
            label="Brightness"
            value={adjustments.brightness}
            onChange={v => setAdjustments(a => ({ ...a, brightness: v }))}
          />
          <AdjustSlider
            icon={<Contrast size={14} />}
            label="Contrast"
            value={adjustments.contrast}
            onChange={v => setAdjustments(a => ({ ...a, contrast: v }))}
          />
          <AdjustSlider
            icon={<Droplets size={14} />}
            label="Saturation"
            value={adjustments.saturation}
            onChange={v => setAdjustments(a => ({ ...a, saturation: v }))}
          />

          <div className="mt-auto pt-4 border-t" style={{ borderColor: 'var(--sage-white-5)' }}>
            <p className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>
              {image.width} x {image.height}px
            </p>
            <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>
              {image.name}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolBtn({ icon, label, onClick, active, accent, disabled }: {
  icon: React.ReactNode; label: string; onClick: () => void; active?: boolean; accent?: boolean; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors disabled:opacity-50"
      style={{
        backgroundColor: accent ? 'var(--sage-primary)' : active ? 'var(--sage-surface)' : 'transparent',
        color: accent ? '#1c1b1a' : active ? 'var(--sage-text)' : 'var(--sage-text-muted)',
      }}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function AdjustSlider({ icon, label, value, onChange }: {
  icon: React.ReactNode; label: string; value: number; onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span style={{ color: 'var(--sage-text-muted)' }}>{icon}</span>
          <span className="text-xs" style={{ color: 'var(--sage-text)' }}>{label}</span>
        </div>
        <span className="text-[10px] tabular-nums" style={{ color: 'var(--sage-text-muted)' }}>{value}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={200}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        className="w-full h-1 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, var(--sage-primary) 0%, var(--sage-primary) ${value / 2}%, var(--sage-white-10) ${value / 2}%, var(--sage-white-10) 100%)`,
        }}
      />
    </div>
  );
}
